import React, { useState } from 'react';
import { useLanguage } from './LanguageContext';
import { apiPost, apiPut, fetchWithAuth, API_URL } from '../utils/api';

const BranchForm = ({ branch, admin, onClose, onSave }) => {
  const { print_lang } = useLanguage();
  const isEdit = !!branch;
  const isSuperAdmin = admin?.is_super_admin;

  const [formData, setFormData] = useState({
    name: branch?.name || '',
    address: branch?.address || '',
    phone: branch?.phone || '',
    email: branch?.email || '',
    manager: branch?.manager || '',
    vat_number: branch?.vat_number || '',
    business_license: branch?.business_license || '',
    dental_license: branch?.dental_license || '',
    open_time: branch?.open_time || '09:00',
    close_time: branch?.close_time || '18:00',
    open_days: branch?.open_days || 'Mon,Tue,Wed,Thu,Fri,Sat',
    language: branch?.language || 'th',
    smartcard_enabled: branch?.smartcard_enabled || 0,
    dentist_limit: branch?.dentist_limit || 10,
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(branch?.logo_path ? `${API_URL}/${branch.logo_path}` : null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const days = [
    { key: 'Mon', label: print_lang('day.monday', 'Monday') },
    { key: 'Tue', label: print_lang('day.tuesday', 'Tuesday') },
    { key: 'Wed', label: print_lang('day.wednesday', 'Wednesday') },
    { key: 'Thu', label: print_lang('day.thursday', 'Thursday') },
    { key: 'Fri', label: print_lang('day.friday', 'Friday') },
    { key: 'Sat', label: print_lang('day.saturday', 'Saturday') },
    { key: 'Sun', label: print_lang('day.sunday', 'Sunday') },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  const handleDayToggle = (dayKey) => {
    const currentDays = formData.open_days ? formData.open_days.split(',') : [];
    const newDays = currentDays.includes(dayKey)
      ? currentDays.filter(d => d !== dayKey)
      : [...currentDays, dayKey];
    setFormData({ ...formData, open_days: newDays.join(',') });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setRemoveLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.address) {
      setError('Name and address are required');
      return;
    }

    setLoading(true);

    try {
      let result;

      if (isEdit) {
        // Update branch
        const updateData = { ...formData };
        if (removeLogo) {
          updateData.remove_logo = true;
        }
        // Only super admin can update dentist_limit
        if (!isSuperAdmin) {
          delete updateData.dentist_limit;
        }
        result = await apiPut(`/api/admin/branches/${branch.branch_id}`, updateData);
      } else {
        // Create branch
        result = await apiPost('/api/admin/branches', formData);
      }

      if (!result.success) {
        setError(result.message || 'Save failed');
        setLoading(false);
        return;
      }

      // Upload logo if selected
      if (logoFile) {
        const branchId = isEdit ? branch.branch_id : result.branch_id;
        const logoData = new FormData();
        logoData.append('logo', logoFile);

        // fetchWithAuth now throws on a non-ok response instead of returning it
        // (so err.code/err.status survive). The branch record above already
        // saved successfully, so a failed logo upload must stay non-fatal here
        // too -- catch locally and warn, same as the old "check .success" path,
        // rather than letting it fall into the outer catch and report a false
        // "Connection error" for a save that actually succeeded.
        try {
          const logoResponse = await fetchWithAuth(`/api/admin/branches/${branchId}/logo`, {
            method: 'POST',
            body: logoData,
          });
          const logoResult = await logoResponse.json();
          if (!logoResult.success) {
            console.warn('Logo upload failed:', logoResult.message);
          }
        } catch (logoErr) {
          console.warn('Logo upload failed:', logoErr.message);
        }
      }

      onSave();
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? print_lang('branch.editBranch', 'Edit Branch') : print_lang('branch.addBranch', 'Add Branch')}
          </h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            {/* Logo Upload */}
            <div className="form-group">
              <label className="form-label">{print_lang('branch.logo', 'Logo')}</label>
              <div className="image-upload" onClick={() => document.getElementById('logo-input').click()}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Preview" className="image-upload-preview" />
                ) : (
                  <div style={{ padding: '2rem 0' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" style={{ margin: '0 auto 0.5rem' }}>
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <p className="image-upload-text">{print_lang('common.uploadImage', 'Click to upload image')}</p>
                  </div>
                )}
                <input
                  type="file"
                  id="logo-input"
                  accept="image/*"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
              </div>
              {logoPreview && (
                <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: '0.5rem' }} onClick={handleRemoveLogo}>
                  {print_lang('common.removeImage', 'Remove Image')}
                </button>
              )}
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{print_lang('branch.name', 'Branch Name')} *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.manager', 'Manager')}</label>
                <input
                  type="text"
                  name="manager"
                  className="form-input"
                  value={formData.manager}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{print_lang('branch.address', 'Address')} *</label>
              <textarea
                name="address"
                className="form-input"
                value={formData.address}
                onChange={handleChange}
                rows="2"
                required
              />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{print_lang('branch.phone', 'Phone')}</label>
                <input
                  type="text"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.email', 'Email')}</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">{print_lang('branch.vatNumber', 'VAT Number')}</label>
                <input
                  type="text"
                  name="vat_number"
                  className="form-input"
                  value={formData.vat_number}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.businessLicense', 'Business License')}</label>
                <input
                  type="text"
                  name="business_license"
                  className="form-input"
                  value={formData.business_license}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.dentalLicense', 'Dental License')}</label>
                <input
                  type="text"
                  name="dental_license"
                  className="form-input"
                  value={formData.dental_license}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{print_lang('branch.openTime', 'Open Time')}</label>
                <input
                  type="time"
                  name="open_time"
                  className="form-input"
                  value={formData.open_time}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.closeTime', 'Close Time')}</label>
                <input
                  type="time"
                  name="close_time"
                  className="form-input"
                  value={formData.close_time}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{print_lang('branch.openDays', 'Open Days')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {days.map((day) => {
                  const isSelected = formData.open_days?.includes(day.key);
                  return (
                    <button
                      key={day.key}
                      type="button"
                      className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => handleDayToggle(day.key)}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid-3">
              <div className="form-group">
                <label className="form-label">{print_lang('branch.language', 'Language')}</label>
                <select
                  name="language"
                  className="form-select"
                  value={formData.language}
                  onChange={handleChange}
                >
                  <option value="th">Thai</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{print_lang('branch.smartcardEnabled', 'Smartcard')}</label>
                <div style={{ paddingTop: '0.5rem' }}>
                  <div
                    className={`toggle ${formData.smartcard_enabled ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, smartcard_enabled: formData.smartcard_enabled ? 0 : 1 })}
                  >
                    <div className="toggle-handle" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {print_lang('branch.dentistLimit', 'Dentist Limit')}
                  {!isSuperAdmin && <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> (Read-only)</span>}
                </label>
                <input
                  type="number"
                  name="dentist_limit"
                  className="form-input"
                  value={formData.dentist_limit}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {print_lang('common.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? print_lang('common.loading', 'Loading...') : print_lang('common.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchForm;
